import { api, type ApiError } from './client';
import type { Role } from './auth';
import { coursesApi, type Marathon } from './courses';
import { isLiveEnrollment } from '@/lib/marathonCards';
import { isOpenCourse } from '@/lib/studentFields';

// Статус учётки. `deactivated` здесь был, но сервер его не пишет и писать не
// будет (договор users-stage4 §1): снимается запись на курс, а не учётка.
export type UserStatus = 'active' | 'blocked' | 'deleted';

export type AdminUser = {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  role: Role;
  status: UserStatus;
  timezone?: string;
  created_at: string;
  updated_at: string;
  // Enriched in admin lists
  marathons_count?: number;
  curator_name?: string;
  // Progress is sourced from progress-service; surfaced here as an optional
  // percentage so the admin list can render a progress column when available.
  progress?: number;
  // Backend (`auth.users`) exposes this as `last_seen_at`. `last_activity_at`
  // is kept as an optional alias for legacy consumers (e.g. curator pages).
  last_seen_at?: string;
  last_activity_at?: string;
  // Подтипы марафонов куратора (D40): в карточке `GET /admin/users/{id}` и, с 28.09.2026, в строках куратора списка
  // `GET /admin/users` — одним запросом на страницу вместо запроса на каждую строку.
  curator_subtypes?: string[];
  /** Только у строк куратора в списке: ученики и разрез по курсам (п. 191). */
  curator_students?: { total: number; by_marathon: { marathon_id: string; title: string; students: number }[] };
  /** Только у строк куратора в списке (28.09.2026): живые группы — по ним ищут на странице кураторов. */
  curator_groups?: { id: string; name: string; marathon_title: string }[];
};

// Mirrors users-service model.MarathonEnrollment (JSON) — the exact shape
// returned by GET /student/enrollments/me and the admin enrollment endpoints.
// NOTE: these rows carry NO marathon title/cover — only marathon_id. To render
// a marathon's name for a student, resolve it via the enrollment-gated tree
// endpoint (coursesApi.getStudentMarathonTree), NOT the public catalog.
/** Итог «Продлить» (11.10.2026): запись и её доп. курсы; пропущенные — с кодом причины. */
export type AccessChange = {
  enrollment_id: string;
  status: string;
  access_from: string;
  access_until: string;
  reactivated: boolean;
  extras_updated: string[];
  extras_skipped: { enrollment_id: string; marathon_id: string; code: string }[];
};

export type Enrollment = {
  id: string;
  student_id: string;
  marathon_id: string;
  tariff_plan_id: string;
  curator_id?: string;
  payment_id?: string;
  is_gift?: boolean;
  /**
   * Основной курс ученика — ровно один. По нему считается уровень английского,
   * и он показывается сверху на экране «Обучение». Мини-курсы из тарифа —
   * дополнительные.
   */
  is_primary?: boolean;
  access_from: string;
  access_until: string;
  original_access_until?: string;
  extension_count?: number;
  /**
   * `inactive` — запись снята с марафона: руками или удалением учётки в корзину
   * (users этап 4). Учётка и остальные курсы при этом живы. `cancelled` и
   * `pending` встречаются только в старых данных.
   */
  status: 'active' | 'expired' | 'inactive' | 'cancelled' | 'pending';
  /**
   * Группа ученика в этом курсе — ровно одна. Куратор берётся ИЗ ГРУППЫ, а не
   * назначается ученику отдельно: у группы кураторов может быть несколько.
   */
  group_id?: string | null;
  // След снятия — только у `inactive`. Группа и куратор снятой записи пусты:
  // они лежат в следе на сервере и возвращаются вместе с записью.
  deactivated_at?: string;
  deactivated_by?: string;
  deactivation_reason?: 'manual' | 'user_removed';
  removal_id?: string;
  created_at?: string;
  updated_at?: string;
};

// Enrollment row enriched with student/curator display names, as returned by
// GET /admin/enrollments?marathon_id=... (the "Кураторы и группы" tab).
// Строка выгрузки доступов.
export type AccessRow = {
  user_id: string;
  full_name: string;
  email: string;
  password?: string;
  reason?: string;
};

export type MarathonEnrollmentRow = Enrollment & {
  student_name: string;
  // Почта = логин ученика. Приходит вместе со списком, чтобы кнопка
  // «скопировать данные для входа» не ходила за ней на каждую строку.
  student_email?: string;
  curator_name?: string;
  group_id?: string | null;
};

// ----- Корзина и снятие с марафона (users этап 4, docs/CONTRACTS/users-stage4.md) -----

/**
 * Удалённая учётка в корзине (§3). У удалённых до этапа 4 (`legacy: true`) даты,
 * автора и причины нет — приходят `null`.
 */
export type DeletedUser = {
  id: string;
  full_name: string;
  email: string;
  role: Role;
  removal_id: string | null;
  removed_at: string | null;
  removed_by: { id: string; name: string } | null;
  source: 'admin' | 'self' | null;
  reason: string | null;
  enrollments_deactivated: number;
  legacy: boolean;
  /** Почту уже носит другая живая учётка — восстановление откажет с `email_taken`. */
  email_conflict: boolean;
  conflict_user_id?: string;
};

export type RemoveUserResult = {
  id: string;
  status: 'deleted';
  already: boolean;
  removal_id: string;
  enrollments_deactivated: number;
};

export type RestoreUserResult = {
  id: string;
  status: string;
  restored_enrollments: number;
  /** Записи, вернувшиеся дополнительными: основной курс уже занят. */
  primary_dropped?: string[];
  /** Не вернулись: пока учётка была в корзине, ученика заново записали на тот же курс. */
  skipped?: { enrollment_id: string; marathon_id: string; reason: string }[];
};

export type DeactivateEnrollmentResult = { enrollment: Enrollment; already: boolean };

export type ActivateEnrollmentResult = {
  enrollment: Enrollment;
  group_restored: boolean;
  curator_restored: boolean;
  primary_dropped: boolean;
  /** Срок доступа уже прошёл — запись активна, но продлить надо отдельно. */
  access_expired: boolean;
};

// A group within a marathon (small cohort of 10-25). Has many curators and many
// students; capacity is a soft target (warn past it, never blocked).
export type GroupCuratorView = { id: string; name: string };
export type MarathonGroup = {
  id: string;
  marathon_id: string;
  name: string;
  capacity: number;
  member_count: number;
  curators: GroupCuratorView[];
};

// Себестоимость AI + активность по одному ученику (только админ).
export type StudentUsage = {
  student_id: string;
  speakings: number; // проведённых (оценённых) живых спикингов, всего
  speakings_logged: number; // из них с залогированной стоимостью
  speaking_minutes: number;
  speaking_cost_usd: number;
  /** Проверок письменных работ моделью и сколько это стоило. */
  writings_checked: number;
  writing_cost_usd: number;
  /**
   * Спринты и словарь — ТОЛЬКО количеством, без денег.
   *
   * Прежняя прикидка «слов × цена за слово» вводила в заблуждение: цифра была
   * выдуманной, а разбор слова оплачивается один раз на всю платформу —
   * второму ученику то же слово достаётся бесплатно.
   */
  sprints: number;
  words: number;
  words_learned: number;
  /** Речь + письмо. Остальное — расход платформы, не ученика. */
  total_cost_usd: number;
  cost_tracked_from: string | null; // YYYY-MM-DD, с какого дня считается $
};

export type AdminUsersListParams = {
  q?: string;
  role?: Role;
  status?: UserStatus;
  marathon_id?: string;
  curator_id?: string;
  offset?: number;
  limit?: number;
};

export type Lead = {
  id: string;
  name: string;
  phone: string;
  email?: string;
  about?: string;
  source: 'web' | 'app';
  age?: number;
  parent_name?: string;
  parent_phone?: string;
  status: 'new' | 'contacted' | 'enrolled' | 'declined';
  note?: string;
  created_at: string;
};

/** Одна массовая загрузка учеников. */
export type StudentImport = {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  tariff_title: string;
  tariff_plan_id?: string | null;
  marathon_id?: string | null;
  duration_days: number;
  total: number;
  created: number;
  existing: number;
  duplicate: number;
  invalid: number;
  failed: number;
  enrolled: number;
  error: string;
  created_at: string;
  finished_at?: string | null;
  /** `file` — загрузка файлом (договор students-import.md), `text` — прежний список. */
  source?: 'text' | 'file';
  file_name?: string;
  already_enrolled?: number;
  errors?: number;
};

/** Что вышло из одной строки списка. */
export type ImportRow = {
  line: number;
  full_name: string;
  email: string;
  state: 'new' | 'existing' | 'duplicate' | 'invalid' | 'failed';
  user_id?: string | null;
  reason?: string;
  /** Только у загрузки файлом: итог строки после записи. */
  action?: 'created' | 'enrolled' | 'already' | 'error' | 'failed';
  reason_code?: string;
  letter?: string;
};

/** Строка файла после пробного прогона (договор students-import.md §2–§3). */
export type FileImportRow = {
  line: number;
  full_name: string;
  email: string;
  phone?: string;
  action: 'create' | 'enroll' | 'already' | 'error';
  reason_code?: string;
  /** Текст для человека — показывать дословно, коды у себя не переводить. */
  reason?: string;
  note?: string;
  user_id?: string;
};

/** Ответ пробного прогона: ничего не записано. */
export type FileImportPreview = {
  dry_run: boolean;
  file: { name: string; sha256: string; rows: number };
  marathon_id: string;
  access_until?: string;
  duration_days: number;
  counts: { create: number; enroll: number; already: number; errors: number };
  rows: FileImportRow[] | null;
  same_file_before: { import_id: string; created_at: string; actor_id: string; status: string }[] | null;
};

export type FileImportInput = { file: File; marathonId: string; accessUntil: string };

function fileImportForm(input: FileImportInput, dryRun: boolean): FormData {
  const form = new FormData();
  form.append('file', input.file, input.file.name);
  form.append('marathon_id', input.marathonId);
  // Пустая дата не отправляется: срок решает сервер по курсу — 90 дней, «Один на один» — 365 (11.10.2026).
  if (input.accessUntil) form.append('access_until', input.accessUntil);
  // ⚠️ Запись — ТОЛЬКО строкой 'false'; всё иное сервер считает пробным прогоном.
  form.append('dry_run', dryRun ? 'true' : 'false');
  return form;
}

/**
 * Число уроков курса для ученика — по дереву курса, а не по полю `lesson_count` (26.09.2026, сборка 61).
 *
 * Каталог отдаёт `lesson_count: 0` всегда (число сервер считает только в админке), дерево — по своим правилам.
 * До этого экран брал поле, «если больше нуля», и дважды показывал «0 сабақ» (06–07.09.2026). В дереве ученика
 * уже только опубликованные уроки — ровно то, что он видит в программе курса. Сторож — `lessonCount.test.ts`.
 */
export function treeLessonCount(m: Pick<Marathon, 'modules'>): number {
  return (m.modules ?? []).reduce((n, md) => n + (md.lessons?.length ?? 0), 0);
}

export const usersApi = {
  /**
   * Заявка на пробное занятие.
   *
   * ⚠️ Ручка ПУБЛИЧНАЯ: человек оставляет контакты до регистрации, он ещё никто
   * для платформы. Источник (сайт или приложение) ставит сервер по заголовку —
   * спрашивать об этом человека незачем.
   */
  /** Заявки для админки. Только для админа и менеджера. */
  async listLeads(params: { status?: string; limit?: number; offset?: number } = {}) {
    const { data } = await api.get('/admin/leads', { params });
    return data as { leads: Lead[]; total: number };
  },
  async setLeadStatus(id: string, status: string, note = '') {
    await api.post(`/admin/leads/${id}/status`, { status, note });
  },
  async createLead(body: {
    name: string;
    phone: string;
    age: number;
    about?: string;
    parent_name?: string;
    parent_phone?: string;
  }) {
    const { data } = await api.post('/leads', body);
    return data as { id: string };
  },

  async list(params: AdminUsersListParams = {}) {
    const { data } = await api.get('/admin/users', { params });
    return data as { items: AdminUser[]; total: number };
  },
  async get(id: string) {
    const { data } = await api.get(`/admin/users/${id}`);
    return data as AdminUser;
  },
  async create(payload: {
    full_name: string;
    email: string;
    password?: string;
    phone?: string;
    role: Role;
  }) {
    const { data } = await api.post('/admin/users', payload);
    return data as AdminUser;
  },
  /** Правка учётки в users. Почты здесь нет: сервер её не пишет — это `changeEmail` (28.09.2026). */
  async update(id: string, payload: Omit<Partial<AdminUser>, 'email'> & { email?: never }) {
    const { data } = await api.put(`/admin/users/${id}`, payload);
    return data as AdminUser;
  },
  async block(id: string) {
    await api.post(`/admin/users/${id}/block`, { blocked: true });
  },
  // Разблокировка — тот же эндпоинт принимает `{ blocked: false }` и переводит
  // пользователя обратно в статус `active`. Без этого блокировка была необратимой
  // из UI.
  async unblock(id: string) {
    await api.post(`/admin/users/${id}/block`, { blocked: false });
  },
  /**
   * Удалить в корзину (§2, только admin): вход закрыт, все активные записи сняты,
   * в корзине строка с причиной. Повтор отвечает `already: true`.
   */
  async remove(id: string, reason?: string) {
    const trimmed = reason?.trim();
    const { data } = await api.delete(`/admin/users/${id}`, trimmed ? { data: { reason: trimmed } } : undefined);
    return data as RemoveUserResult;
  },
  // Просмотр данных для входа (логин) — TЗ §3.3.
  async credentials(id: string) {
    const { data } = await api.get(`/admin/users/${id}/credentials`);
    return data as { email: string; full_name: string; role: string };
  },
  // Отправить приглашение по почте (генерирует временный пароль) — TЗ §3.3.
  async sendInvite(id: string) {
    const { data } = await api.post(`/admin/users/${id}/send-invite`);
    return data as { email: string; password: string; full_name: string };
  },
  // Enrollments
  /**
   * Запись ученика на курс.
   *
   * ⚠️ ТАРИФ НЕОБЯЗАТЕЛЕН (переезд 07.09.2026). Права ученик получает от самого
   * курса, дополнительные курсы выдаются по его настройкам. Поле оставлено для
   * старых вызовов и на случай возврата к тарифам.
   */
  async enroll(payload: {
    student_id: string;
    marathon_id: string;
    tariff_plan_id?: string;
    /** Пусто — срок решает сервер по курсу: 90 дней, «один на один» — 365 (11.10.2026). */
    duration_days?: number;
  }) {
    const { data } = await api.post('/admin/enrollments', payload);
    return data as Enrollment;
  },
  // Массовое зачисление учеников в марафон одним запросом. tariff_plan_id и
  // duration_days опциональны — бэкенд подставит дефолты марафона.
  async bulkEnroll(payload: {
    marathon_id: string;
    student_ids: string[];
    tariff_plan_id?: string | null;
    duration_days?: number;
  }) {
    // Бэкенд может ответить 207 Multi-Status, если часть учеников зачислена, а на
    // остальных операция упала. Раньше вызывающий код видел только { created,
    // skipped } и молча терял «недообработанных» — показывал success-тост. Теперь
    // считаем `failed` (запрошено − создано − пропущено) и пробрасываем флаг
    // частичного результата, чтобы UI показал реальный итог.
    const resp = await api.post('/admin/enrollments/bulk', payload);
    const data = resp.data as { created: Enrollment[]; skipped: unknown[] };
    const created = data.created ?? [];
    const skipped = data.skipped ?? [];
    const failed = Math.max(0, payload.student_ids.length - created.length - skipped.length);
    return {
      created,
      skipped,
      failed,
      partial: resp.status === 207,
    } as { created: Enrollment[]; skipped: unknown[]; failed: number; partial: boolean };
  },
  /**
   * Перевод зачисления на другой тариф того же марафона.
   * duration_days = 0 оставляет срок доступа как был: смена тарифа и продление —
   * два разных решения, склеивать их молча нельзя.
   */
  async setEnrollmentTariff(enrollmentId: string, tariffPlanId: string, durationDays = 0) {
    const { data } = await api.post(`/admin/enrollments/${enrollmentId}/tariff`, {
      tariff_plan_id: tariffPlanId,
      duration_days: durationDays,
    });
    return data as Enrollment;
  },
  /**
   * Выслать ученику код для входа. После 21.08.2026 у большинства пароля нет
   * вовсе, и это правильный ответ на «не могу войти».
   */
  async sendLoginCode(userId: string) {
    const { data } = await api.post(`/admin/users/${userId}/login-code`);
    return data as { retry_after_seconds: number; expires_in_seconds: number };
  },
  /**
   * Задать человеку НОВЫЙ пароль от имени администратора.
   *
   * ⚠️ Посмотреть пароль, придуманный самим человеком, нельзя ни здесь, ни где
   * -либо ещё: в базе от него односторонний отпечаток. Поэтому «задать», а не
   * «показать». Все прежние входы человека при этом гасятся.
   *
   * Требует пароль «как положено»: восемь знаков, заглавная, цифра, особый
   * знак. Если нужен код такого же вида, какой платформа выдаёт сама, — это
   * reissuePassword ниже.
   */
  async setUserPassword(userId: string, password: string) {
    await api.post(`/admin/users/${userId}/password`, { password });
  },
  /**
   * Сменить почту учётки — только admin, служба auth (договор `admin-email-change.md`, 28.09.2026). Правка
   * ученика в users поле почты не пишет. Отказы: `invalid_email`, `same_email`, `user_not_found`, `email_taken`
   * с `holder` — чья учётка держит адрес. Отметка «почта подтверждена» снимается.
   */
  async changeEmail(userId: string, email: string) {
    const { data } = await api.post(`/admin/users/${userId}/email`, { email });
    return data as { email: string };
  },
  /**
   * Завести ученика: платформа сама выдаёт пароль из шести цифр, отправляет
   * человеку письмо с логином, паролем и ссылкой — и возвращает пароль сюда,
   * чтобы администратор мог продиктовать доступ по телефону, не дожидаясь
   * письма.
   *
   * ⚠️ Это НЕ то же, что createUser: тот требует, чтобы пароль придумал
   * администратор, и письма не отправляет вовсе.
   */
  async createStudent(payload: { full_name: string; email: string; phone?: string }) {
    const { data } = await api.post('/admin/students', payload);
    return data as { user: AdminUser; password: string };
  },
  /**
   * Пароль, выданный платформой, для карточки ученика.
   *
   * Пустой ответ — НОРМАЛЬНОЕ состояние, а не поломка: человек сменил пароль
   * сам, и показывать нечего. Причина приходит текстом в `reason`, её и надо
   * показать вместо пустого поля.
   */
  async getIssuedPassword(userId: string) {
    const { data } = await api.get(`/admin/users/${userId}/issued-password`);
    return data as { available: boolean; password?: string; reason?: string };
  },
  /**
   * Снять временную блокировку входа.
   *
   * Блокировка считается по промахам за окно, отдельного признака в карточке
   * нет — сервер убирает сами промахи. Удачные входы остаются: по ним видно,
   * откуда человек заходил.
   */
  async unlockLogin(userId: string) {
    await api.post(`/admin/users/${userId}/unlock`);
  },
  /**
   * Доступы пачкой — для раздачи целому потоку.
   *
   * Пустой пароль в строке НЕ ошибка: человек сменил его сам, и тогда его не
   * знает никто. Причина приходит в `reason` — её и показываем.
   */
  async issuedPasswords(userIds: string[]) {
    const { data } = await api.post('/admin/users/issued-passwords', { user_ids: userIds });
    return data as { items: AccessRow[]; total: number };
  },
  /** Строка списка после разбора на сервере. */
  // (типы объявлены ниже файла — см. BulkRow / BulkPreview / BulkResult)
  /**
   * Показать, что получится из вставленного списка, ДО заведения.
   *
   * Разбирает сервер, а не браузер: предпросмотр и само заведение обязаны
   * считать одним кодом, иначе на экране будет одно, а произойдёт другое.
   */
  async bulkPreview(list: string) {
    const { data } = await api.post('/admin/students/bulk/preview', { list });
    return data as BulkPreview;
  },
  /**
   * Завести всех, кого разбор пометил новыми.
   *
   * Список отправляется текстом заново, а не строками из предпросмотра: между
   * показом и подтверждением кто-то мог зарегистрироваться сам.
   */
  /**
   * Массовая загрузка, живущая НА СЕРВЕРЕ.
   *
   * 🔴 ЗАЧЕМ ОТДЕЛЬНО ОТ `bulkCreate`. Прежний путь шёл двумя запросами из
   * браузера, и на 348 человеках развалился: браузер бросил ждать на 30-й
   * секунде, а зачисление на тариф (второй запрос) не наступило вовсе — люди
   * остались без доступа. Здесь сервер отвечает СРАЗУ, а работу доводит сам.
   */
  async startImport(payload: {
    list: string;
    tariff_plan_id?: string | null;
    marathon_id?: string | null;
    tariff_title?: string;
    duration_days?: number;
  }) {
    const { data } = await api.post('/admin/students/import', payload);
    return data as StudentImport;
  },
  /**
   * Загрузка учеников файлом CSV на марафон (договор students-import.md).
   *
   * Пробный прогон ничего не пишет: ни учёток, ни записей, ни писем. Запись
   * пересчитывает план на сервере заново и отвечает 202 — работа идёт в фоне,
   * отчёт по `getImport`.
   */
  async previewImportFile(input: FileImportInput) {
    const { data } = await api.post('/admin/students/import/file', fileImportForm(input, true), {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    });
    return data as FileImportPreview;
  },
  async startImportFile(input: FileImportInput) {
    const { data } = await api.post('/admin/students/import/file', fileImportForm(input, false), {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    });
    return data as StudentImport;
  },
  /** Шаблон отдаёт сервер с токеном — поэтому запросом, а не ссылкой (см. downloadImportPasswords). */
  async downloadImportTemplate(): Promise<Blob> {
    const res = await api.get('/admin/students/import/template', {
      params: { format: 'csv' },
      responseType: 'blob',
    });
    return res.data as Blob;
  },
  async listImports(limit = 30) {
    const { data } = await api.get('/admin/students/imports', { params: { limit } });
    return ((data as { items?: StudentImport[] }).items ?? []) as StudentImport[];
  },
  async getImport(id: string) {
    const { data } = await api.get(`/admin/students/imports/${id}`);
    return data as { import: StudentImport; rows: ImportRow[] };
  },
  /**
   * Скачать таблицу с паролями.
   *
   * 🔴 ПРОСТОЙ ССЫЛКОЙ ЭТО НЕ РАБОТАЕТ. Пропуск мы передаём заголовком
   * `Authorization`, а не печеньем; обычная ссылка `<a download>` заголовков не
   * шлёт — сервер ответил бы «войдите». Поэтому забираем файл обычным запросом
   * (заголовок подставится сам) и отдаём браузеру уже готовые байты.
   */
  async downloadImportPasswords(id: string): Promise<Blob> {
    const res = await api.get(`/admin/students/imports/${id}/passwords`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },
  async bulkCreate(list: string) {
    const { data } = await api.post('/admin/students/bulk', { list });
    return data as BulkResult;
  },
  /**
   * Выдать ученику новый код вместо забытого — такой же, как при заведении.
   * Прежние входы гасятся, человеку уходит письмо.
   */
  async reissuePassword(userId: string) {
    const { data } = await api.post(`/admin/users/${userId}/reissue-password`);
    return data as { password: string };
  },
  /**
   * Продление: истёкшая запись с концом в будущем снова active, доп. курсы основной записи — следом (11.10.2026).
   * `extras_skipped` — доп. курсы, которые не продлились, с кодом причины.
   */
  async extendAccess(enrollmentId: string, days: number) {
    const { data } = await api.post(`/admin/enrollments/${enrollmentId}/extend`, { days });
    return data as AccessChange;
  },
  async listUserEnrollments(userId: string) {
    const { data } = await api.get(`/admin/users/${userId}/enrollments`);
    return data as Enrollment[];
  },
  // Список зачисленных учеников марафона с именами ученика/куратора — для
  // вкладки «Кураторы и группы» в детали марафона.
  async listMarathonEnrollments(marathonId: string) {
    const { data } = await api.get('/admin/enrollments', {
      params: { marathon_id: marathonId },
    });
    return data as { items: MarathonEnrollmentRow[]; total: number };
  },
  async assignCurator(enrollmentId: string, curatorId: string) {
    await api.post(`/admin/enrollments/${enrollmentId}/assign-curator`, {
      curator_id: curatorId,
    });
  },

  // ⚠️ Пути ниже — буквальные: сторож `adminRoutesInGateway.test.ts` читает их из
  // кода и сверяет со шлюзом и службой. Путь из переменной он не увидит.

  /** Корзина (§3, только admin): свежие удаления сверху, `q` — по почте и имени. */
  async listDeleted(params: { q?: string; limit?: number; offset?: number } = {}) {
    const { data } = await api.get('/admin/users/deleted', { params });
    return data as { items: DeletedUser[]; total: number; offset: number; limit: number };
  },
  /** Восстановить (§4, только admin): вход и записи, снятые этим удалением. */
  async restore(id: string) {
    const { data } = await api.post(`/admin/users/${id}/restore`);
    return data as RestoreUserResult;
  },
  /** Снять запись с марафона (§5, admin и manager). Учётка и другие курсы живы. */
  async deactivateEnrollment(enrollmentId: string) {
    const { data } = await api.post(`/admin/enrollments/${enrollmentId}/deactivate`);
    return data as DeactivateEnrollmentResult;
  },
  /** Вернуть снятую запись (§5, admin и manager): группа — если жива. */
  async activateEnrollment(enrollmentId: string) {
    const { data } = await api.post(`/admin/enrollments/${enrollmentId}/activate`);
    return data as ActivateEnrollmentResult;
  },

  // ----- Расход на ученика (только админ) -----
  // Фактические счётчики активности + себестоимость AI по ученику. Спикинг —
  // точный $ из лога; слова — оценка. GET /admin/students/:id/usage.
  async getStudentUsage(studentId: string) {
    const { data } = await api.get(`/admin/students/${studentId}/usage`);
    return data as StudentUsage;
  },

  // ----- Groups (кураторы и группы внутри марафона) -----
  async listGroups(marathonId: string) {
    const { data } = await api.get(`/admin/marathons/${marathonId}/groups`);
    return data as { items: MarathonGroup[]; total: number };
  },
  // Groups the authenticated curator leads (for the schedule "group" audience).
  async myCuratorGroups() {
    const { data } = await api.get('/curator/groups');
    return data as { items: { id: string; name: string; marathon_id: string; capacity: number }[]; total: number };
  },
  async createGroup(marathonId: string, name: string, capacity: number) {
    const { data } = await api.post(`/admin/marathons/${marathonId}/groups`, { name, capacity });
    return data as MarathonGroup;
  },
  async updateGroup(groupId: string, patch: { name?: string; capacity?: number }) {
    await api.patch(`/admin/groups/${groupId}`, patch);
  },
  async deleteGroup(groupId: string) {
    await api.delete(`/admin/groups/${groupId}`);
  },
  async addGroupCurator(groupId: string, curatorId: string) {
    await api.post(`/admin/groups/${groupId}/curators`, { curator_id: curatorId });
  },
  async removeGroupCurator(groupId: string, curatorId: string) {
    await api.delete(`/admin/groups/${groupId}/curators/${curatorId}`);
  },
  async listGroupMembers(groupId: string) {
    const { data } = await api.get(`/admin/groups/${groupId}/members`);
    return data as { items: MarathonEnrollmentRow[]; total: number };
  },
  async assignGroupMembers(groupId: string, enrollmentIds: string[]) {
    await api.post(`/admin/groups/${groupId}/members`, { enrollment_ids: enrollmentIds });
  },
  async removeGroupMembers(groupId: string, enrollmentIds: string[]) {
    await api.post(`/admin/groups/${groupId}/members/remove`, { enrollment_ids: enrollmentIds });
  },
  /**
   * Перевести запись в другую группу того же курса. Сначала вынимаем из прежней: без этого ученик остался бы в двух
   * сразу, и рассылки по группе пришли бы ему дважды. Одно место на карточку ученика и карточку куратора.
   */
  async moveToGroup(enrollmentId: string, fromGroupId: string | null, toGroupId: string) {
    if (fromGroupId && fromGroupId !== toGroupId) {
      await usersApi.removeGroupMembers(fromGroupId, [enrollmentId]);
    }
    await usersApi.assignGroupMembers(toGroupId, [enrollmentId]);
  },

  // ----- Student self-service enrollment discovery -----
  // The authenticated student's own enrollments (incl. PRIVATE marathons).
  // Backend: GET /student/enrollments/me (== /student/my-marathons) in
  // users-service; returns a bare array of MarathonEnrollment after the client
  // envelope is unwrapped.
  async myEnrollments() {
    const { data } = await api.get('/student/enrollments/me');
    return data as Enrollment[];
  },

  // Convenience: the student's enrolled marathons with title/level/modules
  // resolved. Enrollment rows lack a title, so each marathon is fetched via the
  // enrollment-gated tree endpoint — this works for PRIVATE marathons that the
  // public catalog now hides. Marathons the student genuinely lost access to
  // (403 revoked/unenrolled, 404 deleted) are dropped silently. Transient
  // failures (5xx / network) are propagated so the page can show its error
  // state instead of a misleading "no marathons" empty state. Note: this is one
  // tree request per enrollment.
  async myEnrolledMarathons(): Promise<EnrolledMarathon[]> {
    const enrollments = await this.myEnrollments();
    // Бесплатный марафон открыт КАЖДОМУ вошедшему, записи на него нет — поэтому
    // его не видно в списке записей, и добавляем его отдельно.
    //
    // ЗАЧЕМ. Только что зарегистрировавшийся человек видел пустой раздел «Оқу»:
    // курсы школа выдаёт после оплаты. Пустое приложение — это и потерянный
    // ученик, и риск отказа в магазине по правилу 4.2.
    const free = await coursesApi
      .listPublishedMarathons({ limit: 50 })
      .then((r) => r.items.filter(isOpenCourse)) // `is_open`, раньше `is_free`
      .catch(() => [] as Marathon[]);

    // Признак основного курса нужно ДОНЕСТИ до экрана: до 28.08.2026 его тут
    // теряли, и «основным» на экране «Обучение» становился просто первый в
    // списке. С мини-курсами в тарифе это значило бы, что курс по психологии
    // может встать на место IELTS — молча.
    const primaryIds = new Set(
      enrollments.filter((e) => e.is_primary).map((e) => e.marathon_id),
    );

    const ids = [
      ...enrollments.map((e) => e.marathon_id),
      ...free.map((m) => m.id).filter((id) => !enrollments.some((e) => e.marathon_id === id)),
    ];

    const trees = await Promise.all(
      ids.map((id) =>
        coursesApi.getStudentMarathonTree(id).catch((err: unknown) => {
          const status = (err as ApiError | undefined)?.status;
          if (status === 403 || status === 404) return null;
          throw err;
        }),
      ),
    );
    const loaded = trees.filter((m): m is Marathon => m !== null);

    // Основной — первым: экран показывает сверху именно его. Бесплатный курс,
    // на который записи нет, основным не бывает и идёт в дополнительные.
    // ⚠️ ЧИСЛО УРОКОВ СЧИТАЕМ САМИ. Поле `lesson_count` сервер заполняет только
    // в АДМИНСКИХ списках; ученику приходит дерево курса, где уроки лежат внутри
    // модулей. Из-за этого все дополнительные курсы показывали «0 уроков», хотя
    // уроки в них есть (заказчик, 06.09.2026). Считать по дереву дёшево: оно уже
    // загружено, лишних запросов нет.
    // Только действующие записи: истёкшая или отменённая запись на открытый курс — уже не запись (`isLiveEnrollment`).
    const enrolledIds = new Set(enrollments.filter((e) => isLiveEnrollment(e.status)).map((e) => e.marathon_id));
    return loaded
      .map((m) => ({
        ...m,
        is_primary: primaryIds.has(m.id),
        // Запись на курс есть или нет (02.10.2026). Открытый курс приходит БЕЗ записи: ему не рисуем метку
        // «Мини-курс» и не говорим «Обучение ещё не началось» — это слова про курс из тарифа.
        enrolled: enrolledIds.has(m.id),
        // ⚠️ ТОЛЬКО ДЕРЕВО, ПОЛЕ СЕРВЕРА НЕ ЧИТАЕМ (26.09.2026, сборка 61). До этого здесь было
        // «`lesson_count`, если больше нуля, иначе дерево»: поле приходило то нулём (каталог), то
        // числом по своим правилам, и экран показывал то одно, то другое. Число на карточке —
        // ровно те уроки, что ученик видит в программе курса (`treeLessonCount`).
        lesson_count: treeLessonCount(m),
      }))
      .sort((a, b) => Number(b.is_primary) - Number(a.is_primary));
  },

  // Self-service account deletion (App Store / Google Play requirement).
  // Soft delete on the backend: the account becomes inactive (status "deleted")
  // and can no longer log in. DELETE /student/profile/me.
  async deleteMyAccount(): Promise<void> {
    await api.delete('/student/profile/me');
  },
};

export type CuratorProfile = {
  user_id: string;
  full_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  bio?: string;
  specialization?: string;
  rating: number;
  max_students: number;
  active_students: number;
};

export const curatorProfileApi = {
  async me() {
    const { data } = await api.get('/curator/profile/me');
    return data as CuratorProfile;
  },
  async update(payload: {
    full_name?: string;
    phone?: string;
    avatar_url?: string;
    bio?: string;
    specialization?: string;
  }) {
    const { data } = await api.put('/curator/profile/me', payload);
    return data as CuratorProfile;
  },
  async changePassword(payload: { old_password: string; new_password: string }) {
    await api.put('/curator/profile/password', payload);
  },
};

// Марафон, которым управляет куратор (есть активные ученики), с числом учеников.
// Возвращается GET /curator/marathons — используется в выпадашке создания
// live-уроков/событий, чтобы куратор выбирал только марафоны со своей аудиторией
// (BUG 1: раньше список брался из всего публичного каталога).
export type CuratorMarathon = {
  marathon_id: string;
  title: string;
  student_count: number;
};

// Single student as seen by their curator — GET /curator/students/:id returns
// the StudentProfile enriched with identity fields (CuratorStudentView on the
// backend). Read-only; curators have no admin actions here.
//
// ⚠️ `points` И `current_level` УДАЛЕНЫ ОТСЮДА НАМЕРЕННО (12.09.2026, договор
// `student-points.md` §5) — И ЭТО СТОРОЖ. Сервер их больше не присылает: баллы в
// той колонке не пишет ни одна служба (на проде ноль у всех 2058 профилей), а
// уровень равен 'A1' у 2056 из 2058. Пока поля стояли в типе, экран мог их
// прочитать и снова показать ноль как правду; теперь `npm run build` этого не
// даст. Баллы — у progress-service (`studentPointsQuery` в `api/progress.ts`).
export type CuratorStudentDetail = {
  user_id: string;
  full_name?: string;
  email?: string;
  avatar_url?: string;
  goals?: string;
  bio?: string;
  target_language?: string;
  created_at?: string;
  last_activity_at?: string;
  /** Записи ученика у этого куратора — только в списке «Мои ученики», для поиска по курсу и группе (28.09.2026). */
  enrollments?: { marathon_title: string; group_name: string; status: string }[];
};

export const curatorApi = {
  async listMyMarathons() {
    const { data } = await api.get('/curator/marathons');
    return data as { items: CuratorMarathon[]; total: number };
  },
  // All students assigned to this curator — used by the chat page to start a new
  // conversation with a student who hasn't messaged yet.
  async listStudents() {
    const { data } = await api.get('/curator/students');
    const raw = ((data as { items?: CuratorStudentDetail[] })?.items ?? data ?? []) as CuratorStudentDetail[];
    return Array.isArray(raw) ? raw : [];
  },
  async getStudent(id: string) {
    const { data } = await api.get(`/curator/students/${id}`);
    return data as CuratorStudentDetail;
  },
};

/** Что платформа сделает со строкой списка при массовом добавлении. */
export type BulkRowState = 'new' | 'existing' | 'duplicate' | 'invalid';

export type BulkRow = {
  /** Номер строки так, как её видит человек в своём списке, считая пустые. */
  line: number;
  full_name: string;
  email: string;
  state: BulkRowState;
  existing_id?: string;
  reason?: string;
};

export type BulkPreview = {
  rows: BulkRow[];
  new: number;
  existing: number;
  duplicate: number;
  invalid: number;
};

export type BulkCreated = {
  id: string;
  full_name: string;
  email: string;
  /** Выданный пароль — для выгрузки: письмо может уйти в спам. */
  password: string;
};

export type BulkResult = {
  created: BulkCreated[];
  skipped: BulkRow[];
  failed: BulkRow[];
};

/**
 * Курс ученика вместе с признаком «основной».
 *
 * Основной ровно один: по нему считается уровень английского, и он показывается
 * сверху на экране «Обучение». Мини-курсы (курс о поступлении, занятия с
 * психологом) — дополнительные: в уровень и рейтинг они не идут.
 */
export type EnrolledMarathon = Marathon & {
  is_primary: boolean;
  /** `false` — открытый курс без записи (его видит каждый вошедший). Нет поля — считаем, что запись есть. */
  enrolled?: boolean;
};
